import { Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';

/**
 * Area-of-Effect System (M20)
 *
 * Creates and manages AoE effects at world positions.
 * Visual: expanding colored ring that fades out.
 * Handles damage, knockback, status effects, and friendly fire control.
 */
export class AoESystem {
  /**
   * @param {import('pixi.js').Container} worldContainer - The world container to add visuals to
   */
  constructor(worldContainer) {
    this.worldContainer = worldContainer;
    this.activeEffects = []; // visual effects currently animating
  }

  /**
   * Create an AoE effect at a world position.
   * Immediately checks all provided targets and applies damage/effects.
   *
   * @param {number} x - Center X in world pixels
   * @param {number} y - Center Y in world pixels
   * @param {number} radius - Effect radius in pixels
   * @param {number} damage - Damage dealt to entities within radius
   * @param {object} [options] - Additional options
   * @param {number} [options.color=0xff4444] - Visual color
   * @param {number} [options.duration=0.4] - Visual effect duration in seconds
   * @param {string} [options.statusEffect] - Status effect ID to apply on hit
   * @param {boolean} [options.friendlyFire=false] - If true, can damage the source entity
   * @param {number} [options.knockback=0] - Knockback strength applied to hit entities
   * @param {object[]} [options.targets=[]] - Array of entities to check for hits
   * @param {object} [options.source] - The entity that created the AoE (for knockback direction)
   * @param {object} [options.statusEffectSystem] - StatusEffectSystem instance for applying effects
   * @param {object} [options.damageNumbers] - DamageNumbers instance for spawning numbers
   * @returns {{ hitEntities: object[] }} Entities that were hit
   */
  createAoE(x, y, radius, damage, options = {}) {
    const {
      color = 0xff4444,
      duration = 0.4,
      statusEffect = null,
      friendlyFire = false,
      knockback = 0,
      targets = [],
      source = null,
      statusEffectSystem = null,
      damageNumbers = null,
    } = options;

    const hitEntities = [];

    // Check targets within radius
    for (const target of targets) {
      if (!target) continue;

      // Skip dead entities
      if (target.isAlive !== undefined && !target.isAlive) continue;
      if (target.state === 'dead') continue;

      // Skip source entity unless friendlyFire is enabled
      if (!friendlyFire && target === source) continue;

      // Calculate distance from AoE center to target center
      const targetCX = target.x + TILE_SIZE / 2;
      const targetCY = target.y + TILE_SIZE / 2;
      const dx = targetCX - x;
      const dy = targetCY - y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radius) {
        // Apply damage
        if (damage > 0) {
          if (target.takeDamage) {
            // Calculate knockback direction (away from center)
            let kbNx = 0;
            let kbNy = 0;
            if (dist > 0) {
              kbNx = dx / dist;
              kbNy = dy / dist;
            }

            const kbStrength = knockback || 1;
            target.takeDamage(damage, kbNx * kbStrength, kbNy * kbStrength);

            // Spawn damage number
            if (damageNumbers) {
              damageNumbers.spawn(targetCX, targetCY - 8, damage, 0xffffff);
            }
          }
        }

        // Apply status effect
        if (statusEffect && statusEffectSystem) {
          statusEffectSystem.apply(target, statusEffect);
        }

        hitEntities.push(target);
      }
    }

    // Create visual effect: expanding ring that fades out
    this._createVisual(x, y, radius, color, duration);

    return { hitEntities };
  }

  /**
   * Create a visual-only AoE ring (no damage). Used for previews or effects.
   * @param {number} x
   * @param {number} y
   * @param {number} radius
   * @param {number} color
   * @param {number} duration
   */
  _createVisual(x, y, radius, color, duration) {
    const gfx = new Graphics();

    // Draw a filled circle with transparency
    gfx.circle(0, 0, radius);
    gfx.fill({ color: color, alpha: 0.35 });

    // Draw an outer ring
    gfx.circle(0, 0, radius);
    gfx.stroke({ width: 2, color: color, alpha: 0.6 });

    gfx.x = x;
    gfx.y = y;
    this.worldContainer.addChild(gfx);

    this.activeEffects.push({
      gfx,
      timer: duration,
      maxTimer: duration,
    });
  }

  /**
   * Update visual effects: fade and scale expanding rings.
   * @param {number} dt - Delta time in seconds
   */
  update(dt) {
    for (let i = this.activeEffects.length - 1; i >= 0; i--) {
      const fx = this.activeEffects[i];
      fx.timer -= dt;

      const progress = 1 - (fx.timer / fx.maxTimer);
      fx.gfx.alpha = Math.max(0, 1 - progress);

      // Scale up slightly as it fades
      const scale = 1 + progress * 0.3;
      fx.gfx.scale.set(scale, scale);

      if (fx.timer <= 0) {
        if (fx.gfx.parent) {
          this.worldContainer.removeChild(fx.gfx);
        }
        fx.gfx.destroy();
        this.activeEffects.splice(i, 1);
      }
    }
  }

  /**
   * Clean up all active visual effects.
   */
  destroy() {
    for (const fx of this.activeEffects) {
      if (fx.gfx.parent) {
        this.worldContainer.removeChild(fx.gfx);
      }
      fx.gfx.destroy();
    }
    this.activeEffects = [];
  }
}
